(() => {
  if (customElements.get('facet-sidebar')) return;
  const formIds = ['SidebarfacetForm', 'FacetSortForm'];

  class FacetSidebarForm extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.addEventListener('submit', (event) => this.onSubmitHandler(event), { signal });
      this.addEventListener('input', (event) => {
        const form = this.eventForm(event);
        if (!form || !this.autoSubmit(form)) return;
        if (event.target.matches('select, input[type="radio"], input[type="checkbox"]')) return;
        this.queueSubmit(event);
      }, { signal });
      this.addEventListener('change', (event) => {
        const form = this.eventForm(event);
        if (!form || !this.autoSubmit(form)) return;
        if (event.target.matches('select, input[type="radio"], input[type="checkbox"]')) this.onSubmitHandler(event);
      }, { signal });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.listeners = null;
      clearTimeout(this.submitTimer);
    }

    eventForm(event) {
      const form = event.target.closest?.('form');
      return form && this.contains(form) && formIds.includes(form.id) ? form : null;
    }

    autoSubmit(form) { return form.id === 'FacetSortForm' || window.innerWidth >= 768; }

    queueSubmit(event) {
      clearTimeout(this.submitTimer);
      this.submitTimer = setTimeout(() => {
        const form = this.eventForm(event);
        if (this.isConnected && form && this.autoSubmit(form)) this.onSubmitHandler(event);
      }, 500);
    }

    createSearchParams(form) {
      const params = new URLSearchParams(new FormData(form));
      form.querySelectorAll('price-range input[type="number"][name]').forEach((input) => {
        const value = params.get(input.name);
        if (value === null || value === '') return;
        if (input.classList.contains('field__input_min') && Number(value) <= Number(input.min || 0)) params.delete(input.name);
        if (input.classList.contains('field__input_max') && input.max !== '' && Number(value) >= Number(input.max)) params.delete(input.name);
      });
      return params;
    }

    mergeSearchParams(form, params) {
      this.createSearchParams(form).forEach((value, key) => {
        if (key === 'page' || key === 'section_id' || value === '') return;
        if (key.startsWith('filter.')) {
          if (!params.getAll(key).includes(value)) params.append(key, value);
        } else params.set(key, value);
      });
      return params;
    }

    onSubmitHandler(event) {
      if (!this.eventForm(event)) return;
      event.preventDefault();
      clearTimeout(this.submitTimer);
      // Keep q/type/options from a search page; forms replace filters/sort and reset pagination.
      let params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith('filter.') || ['sort_by', 'page', 'section_id'].includes(key)) params.delete(key);
      });
      formIds.forEach((id) => {
        const form = document.getElementById(id);
        if (form) params = this.mergeSearchParams(form, params);
      });
      return this.onSubmitForm(params.toString(), event);
    }

    onSubmitForm(searchParams, event) { return FacetSidebarForm.renderPage(searchParams, event); }

    onActiveFilterClick(event) {
      const anchor = event.target?.closest?.('a[href]') || event.currentTarget;
      if (!anchor?.href) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      event.preventDefault();
      return FacetSidebarForm.renderPage(url.searchParams.toString(), event);
    }

    static setListeners() {
      window.addEventListener('popstate', () => {
        // The URL is authoritative, including native history entries with null/empty state.
        FacetSidebarForm.renderPage(window.location.search.slice(1), null, false);
      });
    }

    static getSections() {
      const section = document.getElementById('product-card-grid')?.dataset.id;
      return section ? [{ section }] : [];
    }

    static setLoading(busy) {
      document.querySelector('#products-container .product-card-grid')?.classList.toggle('loading', busy);
      document.getElementById('products-container')?.setAttribute('aria-busy', String(busy));
    }

    static showError(searchParams) {
      const container = document.getElementById('products-container');
      if (!container) return;
      container.querySelector('[data-facet-error]')?.remove();
      const message = document.createElement('p');
      message.setAttribute('data-facet-error', '');
      message.setAttribute('role', 'alert');
      message.append(document.createTextNode('No pudimos actualizar los resultados. '));
      const retry = document.createElement('a');
      retry.href = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
      retry.textContent = 'Volver a intentarlo';
      message.append(retry);
      container.prepend(message);
    }

    static async renderPage(searchParams, event, updateURLHash = true) {
      const id = ++FacetSidebarForm.requestId;
      FacetSidebarForm.controller?.abort();
      const controller = new AbortController();
      FacetSidebarForm.controller = controller;
      const section = FacetSidebarForm.getSections()[0]?.section;
      if (!section || !document.getElementById('products-container')) return false;
      FacetSidebarForm.setLoading(true);
      document.querySelector('[data-facet-error]')?.remove();
      const params = new URLSearchParams(searchParams);
      params.delete('section_id');
      const normalized = params.toString();
      const closeOnSuccess = event?.type === 'submit' && event.target.closest?.('form')?.id === 'SidebarfacetForm' && window.innerWidth < 768;
      const urlParams = new URLSearchParams(params);
      urlParams.set('section_id', section);
      const url = `${window.location.pathname}?${urlParams}`;
      try {
        let html = FacetSidebarForm.filterData.find((item) => item.url === url)?.html;
        if (html === undefined) {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Filter request failed');
          html = await response.text();
        }
        if (controller.signal.aborted || id !== FacetSidebarForm.requestId) return false;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById('products-container') || !parsed.getElementById('product-card-grid')) throw new Error('Missing filter section');
        FacetSidebarForm.renderFilters(parsed, event);
        FacetSidebarForm.renderProductGridContainer(parsed);
        FacetSidebarForm.renderProductCount(parsed);
        FacetSidebarForm.filterData = [
          ...FacetSidebarForm.filterData.filter((item) => item.url !== url), { html, url },
        ].slice(-20);
        FacetSidebarForm.searchParamsPrev = normalized;
        if (updateURLHash) FacetSidebarForm.updateURLHash(normalized);
        document.dispatchEvent(new CustomEvent('collection:reloaded', { detail: { searchParams: normalized } }));
        if (closeOnSuccess) document.getElementById('facets-filte-drawer')?.dispatchEvent(new CustomEvent('facets:request-close', { bubbles: true }));
        return true;
      } catch (error) {
        if (!controller.signal.aborted && id === FacetSidebarForm.requestId) FacetSidebarForm.showError(normalized);
        return false;
      } finally {
        if (id === FacetSidebarForm.requestId) {
          FacetSidebarForm.setLoading(false);
          FacetSidebarForm.controller = null;
        }
      }
    }

    static parsed(html) { return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html; }

    static renderProductGridContainer(html) {
      const parsed = FacetSidebarForm.parsed(html);
      const target = document.getElementById('products-container');
      const source = parsed.getElementById('products-container');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    static renderProductCount(html) {
      const parsed = FacetSidebarForm.parsed(html);
      const target = document.getElementById('facet-product-count');
      const source = parsed.getElementById('facet-product-count');
      if (target) {
        target.innerHTML = source?.innerHTML || '';
        target.hidden = !source;
      }
      if (source) {
        const text = source.textContent.replace(/\s+/g, ' ').trim();
        document.querySelectorAll('.vr-result-count, .vr-collection-mobile-count').forEach((item) => { item.textContent = text; });
      }
      const selectedCount = parsed.querySelector('.vr-collection-tools .vr-active-count') || parsed.querySelector('.vr-active-count');
      if (selectedCount) document.querySelectorAll('.vr-active-count').forEach((item) => { item.textContent = selectedCount.textContent; });
    }

    static renderFilters(html) {
      const parsed = FacetSidebarForm.parsed(html);
      const active = document.activeElement;
      const focusedForm = active?.closest?.('form');
      const focus = focusedForm && formIds.includes(focusedForm.id)
        ? { form: focusedForm.id, id: active.id, name: active.name, value: active.value } : null;
      formIds.forEach((id) => {
        const target = document.getElementById(id);
        const source = parsed.getElementById(id);
        if (!target || !source) return;
        const open = new Map(Array.from(target.querySelectorAll('details')).map((item) => [item.id || item.dataset.index, item.open]).filter(([key]) => key));
        target.innerHTML = source.innerHTML;
        target.querySelectorAll('details').forEach((item) => {
          const key = item.id || item.dataset.index;
          if (open.has(key)) item.open = open.get(key);
        });
      });
      FacetSidebarForm.renderActiveFacets(parsed);
      if (focus) {
        const form = document.getElementById(focus.form);
        const candidate = focus.id ? document.getElementById(focus.id)
          : Array.from(form?.querySelectorAll('[name]') || []).find((item) => item.name === focus.name && item.value === focus.value);
        if (candidate && form?.contains(candidate) && !candidate.disabled) candidate.focus({ preventScroll: true });
      }
    }

    static renderActiveFacets(html) {
      const parsed = FacetSidebarForm.parsed(html);
      const source = parsed.querySelector('.active-facet');
      document.querySelectorAll('.active-facet').forEach((target) => { target.innerHTML = source?.innerHTML || ''; });
    }

    static updateURLHash(searchParams) {
      const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}${window.location.hash}`;
      if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) history.pushState({ searchParams }, '', url);
    }
  }
  FacetSidebarForm.filterData = [];
  FacetSidebarForm.searchParamsInitial = window.location.search.slice(1);
  FacetSidebarForm.searchParamsPrev = window.location.search.slice(1);
  FacetSidebarForm.requestId = 0;
  customElements.define('facet-sidebar', FacetSidebarForm);
  FacetSidebarForm.setListeners();

  class FacetRemove extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.addEventListener('click', (event) => {
        const anchor = event.target.closest('a[href]');
        if (anchor && this.contains(anchor)) FacetSidebarForm.prototype.onActiveFilterClick(event);
      }, { signal: this.listeners.signal });
    }
    disconnectedCallback() { this.listeners?.abort(); this.listeners = null; }
  }
  customElements.define('facet-remove', FacetRemove);

  class PriceSlider extends HTMLElement {
    connectedCallback() {
      const slider = this.querySelector('.range-slider');
      const amounts = this.querySelector('.facets-price-ranges');
      if (!slider || !amounts || typeof noUiSlider === 'undefined') return;
      slider.noUiSlider?.destroy();
      const min = Number(slider.dataset.min);
      const max = Number(slider.dataset.max);
      const start = [Number(slider.dataset.minValue), Number(slider.dataset.maxValue)];
      if (![min, max, ...start].every(Number.isFinite) || max <= min) return;
      noUiSlider.create(slider, { start, connect: true, step: 1, range: { min, max } });
      this.slider = slider;
      slider.noUiSlider.on('update', (values) => {
        const low = amounts.querySelector('.field__input_min');
        const high = amounts.querySelector('.field__input_max');
        if (low) low.value = values[0];
        if (high) high.value = values[1];
      });
      slider.noUiSlider.on('change', () => {
        this.closest('facet-sidebar')?.querySelector('form')?.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
    disconnectedCallback() { this.slider?.noUiSlider?.destroy(); }
  }
  customElements.define('price-range', PriceSlider);

  class ShowMoreFilterButton extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.show-more-button');
        const filter = this.closest('.facets-accordion');
        if (!button || !filter) return;
        event.preventDefault();
        const expanded = filter.getAttribute('aria-expanded') !== 'true';
        filter.setAttribute('aria-expanded', String(expanded));
        button.setAttribute('aria-expanded', String(expanded));
        filter.querySelector('.more-items')?.classList.toggle('hidden', !expanded);
        this.querySelectorAll('.visible-hidden').forEach((item) => item.classList.toggle('hidden'));
      }, { signal: this.listeners.signal });
    }
    disconnectedCallback() { this.listeners?.abort(); this.listeners = null; }
  }
  customElements.define('show-more-button', ShowMoreFilterButton);

  class FacetsDrawer extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-drawer-head]');
        if (trigger && this.contains(trigger)) this.openDrawer(event, trigger);
      }, { signal });
      document.addEventListener('click', (event) => {
        if (this.drawer?.classList.contains('open') && event.target.closest('[data-close-drawer]') && this.drawer.contains(event.target)) this.closeDrawer(event);
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer?.classList.contains('open')) this.closeDrawer(event);
      }, { signal });
      document.addEventListener('facets:request-close', (event) => {
        if (event.target === this.drawer) this.closeDrawer(event);
      }, { signal });
    }

    disconnectedCallback() {
      this.closeDrawer(null, false);
      this.listeners?.abort();
      this.listeners = null;
    }

    openDrawer(event, trigger = this.querySelector('[data-drawer-head]')) {
      event?.preventDefault();
      const drawer = document.getElementById('facets-filte-drawer');
      if (!drawer || !trigger) return;
      if (this.drawer === drawer && drawer.classList.contains('open') && this.trigger === trigger) return;
      if (FacetsDrawer.active && FacetsDrawer.active !== this) FacetsDrawer.active.closeDrawer(null, false);
      this.drawer = drawer;
      this.trigger = trigger;
      FacetsDrawer.active = this;
      this.addedScrollLock = !document.body.classList.contains('no-scroll');
      document.body.classList.add('no-scroll');
      drawer.style.transform = 'translateY(0)';
      drawer.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        if (this.drawer?.classList.contains('open') && typeof trapFocusElements === 'function') trapFocusElements(this.drawer);
      });
    }

    closeDrawer(event, restoreFocus = true) {
      if (!this.drawer?.classList.contains('open')) return;
      event?.preventDefault();
      cancelAnimationFrame(this.focusFrame);
      this.drawer.classList.remove('open');
      this.drawer.style.removeProperty('transform');
      this.drawer.querySelector('.drawer-inner-card')?.style.removeProperty('transform');
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.addedScrollLock) document.body.classList.remove('no-scroll');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      if (FacetsDrawer.active === this) FacetsDrawer.active = null;
    }
  }
  customElements.define('facets-drawer', FacetsDrawer);

  class FacetsFilterDrawer extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.addEventListener('touchstart', (event) => {
        this.start = null;
        if (!this.classList.contains('open') || event.target.closest('input, select, textarea, button, a, .noUi-target')) return;
        this.start = { x: event.touches[0].clientX, y: event.touches[0].clientY, distance: 0 };
      }, { passive: true, signal });
      this.addEventListener('touchmove', (event) => {
        if (!this.start) return;
        const distance = event.touches[0].clientY - this.start.y;
        const horizontal = Math.abs(event.touches[0].clientX - this.start.x);
        const scrollable = event.target.closest('.drawer-content-contain');
        if (distance <= horizontal || (scrollable && scrollable.scrollTop > 0)) return;
        this.start.distance = distance;
        const card = this.querySelector('.drawer-inner-card');
        if (card) card.style.transform = `translateY(${distance}px)`;
        if (event.cancelable) event.preventDefault();
      }, { passive: false, signal });
      const finish = () => {
        if (this.start?.distance > 100) this.dispatchEvent(new CustomEvent('facets:request-close', { bubbles: true }));
        this.querySelector('.drawer-inner-card')?.style.removeProperty('transform');
        this.start = null;
      };
      this.addEventListener('touchend', finish, { signal });
      this.addEventListener('touchcancel', () => {
        this.querySelector('.drawer-inner-card')?.style.removeProperty('transform');
        this.start = null;
      }, { signal });
    }
    disconnectedCallback() { this.listeners?.abort(); this.listeners = null; this.start = null; }
  }
  if (!customElements.get('facets-filter-drawer')) customElements.define('facets-filter-drawer', FacetsFilterDrawer);
})();

